import React from 'react'
import { motion } from 'framer-motion'
import { Utensils, QrCode, Monitor, Receipt, Server } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export const BaresHeroSection: React.FC = () => {
  const { t } = useTranslation('bares')

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pt-24">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-64 h-64 bg-amber-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-200/20 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-6">
              <Utensils className="w-4 h-4" />
              {t('hero.kicker')}
            </div>

            <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
              <span className="text-gray-900">{t('hero.title')}</span>
              <br />
              <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
                {t('hero.subtitle', 'Gestão Completa para Bares e Restaurantes')}
              </span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {t('hero.description')}
            </p>

            {/* Key Benefits */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('hero.badges.qr')}</div>
                  <div className="text-sm text-gray-600">{t('hero.benefits.qr', 'Sem garçom')}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('hero.badges.kds')}</div>
                  <div className="text-sm text-gray-600">{t('hero.benefits.kds', 'Cozinha ágil')}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('hero.badges.split')}</div>
                  <div className="text-sm text-gray-600">{t('hero.benefits.split', 'Por pessoa')}</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Server className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{t('hero.badges.erp')}</div>
                  <div className="text-sm text-gray-600">{t('hero.benefits.erp', 'Gestão total')}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Visual Showcase */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Real Dashboard Screenshot */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <img
                  src="/images/bares/website.png"
                  alt={t('hero.images.website', 'Site do Restaurante')}
                  className="w-full h-auto"
                />
              </div>

              {/* Mobile App Real Screenshot */}
              <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-xl p-2 transform -rotate-12 hover:rotate-0 transition-transform duration-500">
                <img
                  src="/images/bares/04-menu-lista-mobile.png"
                  alt={t('hero.images.mobile', 'Cardápio Mobile')}
                  className="w-32 h-auto rounded-lg"
                />
                <div className="text-xs text-center mt-2 font-medium text-gray-700">{t('hero.images.app', 'App Mobile')}</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Impact Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
        >
          <div className="text-center">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <QrCode className="w-6 h-6 text-amber-600" />
            </div>
            <div className="font-bold text-gray-900 mb-1">{t('hero.stats.qr.title', 'Pedidos QR')}</div>
            <div className="text-sm text-gray-600">{t('hero.stats.qr.subtitle', 'Autoatendimento')}</div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Monitor className="w-6 h-6 text-orange-600" />
            </div>
            <div className="font-bold text-gray-900 mb-1">{t('hero.stats.kds.title', 'KDS Integrado')}</div>
            <div className="text-sm text-gray-600">{t('hero.stats.kds.subtitle', 'Cozinha rápida')}</div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Receipt className="w-6 h-6 text-green-600" />
            </div>
            <div className="font-bold text-gray-900 mb-1">{t('hero.stats.split.title', 'Conta Dividida')}</div>
            <div className="text-sm text-gray-600">{t('hero.stats.split.subtitle', 'Por pessoa')}</div>
          </div>

          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Server className="w-6 h-6 text-purple-600" />
            </div>
            <div className="font-bold text-gray-900 mb-1">{t('hero.stats.erp.title', 'ERP Completo')}</div>
            <div className="text-sm text-gray-600">{t('hero.stats.erp.subtitle', 'Tudo integrado')}</div>
          </div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm text-gray-500 font-medium">{t('hero.scrollIndicator', 'Role para explorar')}</span>
          <div className="w-0.5 h-8 bg-gradient-to-b from-amber-600 to-transparent animate-pulse"></div>
        </div>
      </motion.div>
    </section>
  )
}
